import { useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, Check, Plus, X, Scissors, Play } from "lucide-react";
import { thumbUrl, streamUrl, titleCase, saveYear } from "../lib/api";
import { useVideoEditor } from "../hooks/useVideoEditor";
import { chipColor } from "./VideoList";

const fallbackThumb =
  "https://images.unsplash.com/photo-1681311311317-a0561a8eef74?crop=entropy&cs=srgb&fm=jpg&w=600&q=70";

export default function EditVideo({ video, allTags, onBack, onSplit, refresh }) {
  const { name, updateName, savedName, tags, busy, saveCurrentName, applyTag, removeCurrentTag } =
    useVideoEditor(video, refresh);
  const [newTag, setNewTag] = useState("");
  const [thumbSrc, setThumbSrc] = useState(thumbUrl(video.ratingKey));
  const [playing, setPlaying] = useState(false);
  const [year, setYear] = useState(video.year ? String(video.year) : "");
  const [yearSaved, setYearSaved] = useState(false);
  const [yearBusy, setYearBusy] = useState(false);

  const handleSaveYear = async () => {
    setYearBusy(true);
    try {
      const y = year.trim() === "" ? null : parseInt(year, 10);
      await saveYear(video.ratingKey, y);
      setYearSaved(true);
      setTimeout(() => setYearSaved(false), 3000);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the year. Please try again.");
    } finally {
      setYearBusy(false);
    }
  };

  const handleCreateTag = async () => {
    const formatted = titleCase(newTag);
    if (!formatted) return;
    setNewTag("");
    await applyTag(formatted);
  };

  const paletteTags = allTags.filter((t) => !tags.includes(t));

  return (
    <div className="min-h-screen">
      <header className="glass sticky top-0 z-30 border-b border-white/5">
        <div className="mx-auto max-w-3xl px-5 sm:px-8 py-3.5">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-1 rounded-lg py-1.5 pr-3 text-[17px] font-medium text-[#0A84FF] hover:opacity-80 transition-opacity"
            data-testid="back-button"
          >
            <ChevronLeft className="h-6 w-6" />
            Videos
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 sm:px-8 pb-20">
        {/* Hero — tap to play */}
        <div className="relative mt-5 overflow-hidden rounded-3xl bg-black ring-1 ring-white/10 shadow-2xl shadow-black/50 animate-fade-up">
          {playing ? (
            <video
              src={streamUrl(video.ratingKey)}
              controls
              autoPlay
              playsInline
              className="w-full aspect-video bg-black"
              data-testid="edit-player"
            />
          ) : (
            <button
              onClick={() => setPlaying(true)}
              className="group block w-full text-left"
              data-testid="play-video-button"
            >
              <img
                src={thumbSrc}
                alt={video.name}
                onError={() => setThumbSrc(fallbackThumb)}
                className="w-full aspect-video object-cover bg-[#1c1c1e]"
                data-testid="edit-thumbnail"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
              <span className="absolute inset-0 grid place-items-center">
                <span className="grid h-[72px] w-[72px] place-items-center rounded-full bg-black/55 ring-1 ring-white/30 backdrop-blur-sm transition-transform duration-200 group-hover:scale-110">
                  <Play className="h-8 w-8 translate-x-0.5 text-white" fill="white" />
                </span>
              </span>
              <h1 className="absolute bottom-5 left-6 right-6 text-3xl font-bold tracking-tight text-white drop-shadow">
                {video.name}
              </h1>
            </button>
          )}
        </div>

        {/* NAME */}
        <section className="mt-10">
          <h2 className="text-[15px] font-semibold uppercase tracking-[0.12em] text-white/40 mb-3">
            Name
          </h2>
          <input
            type="text"
            value={name}
            onChange={(e) => updateName(e.target.value)}
            className="h-14 w-full rounded-2xl bg-white/[0.08] px-5 text-xl text-white outline-none ring-1 ring-white/10 focus:ring-2 focus:ring-[#0A84FF] transition-all"
            data-testid="name-input"
          />
          <button
            onClick={saveCurrentName}
            disabled={busy}
            className={`mt-3 inline-flex h-14 w-full items-center justify-center gap-2 rounded-2xl text-lg font-semibold text-white transition-colors disabled:opacity-60 ${
              savedName ? "bg-[#30D158]" : "bg-[#0A84FF] hover:bg-[#409CFF]"
            }`}
            data-testid="save-name-button"
          >
            {savedName ? (
              <>
                <Check className="h-5 w-5" /> Saved
              </>
            ) : (
              "Save name"
            )}
          </button>
        </section>

        {/* YEAR */}
        <section className="mt-10">
          <h2 className="text-[15px] font-semibold uppercase tracking-[0.12em] text-white/40 mb-3">
            Year
          </h2>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="number"
              inputMode="numeric"
              value={year}
              onChange={(e) => {
                setYear(e.target.value);
                setYearSaved(false);
              }}
              onKeyDown={(e) => e.key === "Enter" && handleSaveYear()}
              placeholder="e.g. 1994"
              className="h-14 flex-1 rounded-2xl bg-white/[0.08] px-5 text-xl text-white placeholder-white/35 outline-none ring-1 ring-white/10 focus:ring-2 focus:ring-[#0A84FF] transition-all"
              data-testid="year-input"
            />
            <button
              onClick={handleSaveYear}
              disabled={yearBusy}
              className={`inline-flex h-14 items-center justify-center gap-2 rounded-2xl px-7 text-lg font-semibold text-white transition-colors disabled:opacity-60 ${
                yearSaved ? "bg-[#30D158]" : "bg-[#0A84FF] hover:bg-[#409CFF]"
              }`}
              data-testid="save-year-button"
            >
              {yearSaved ? (
                <>
                  <Check className="h-5 w-5" /> Saved
                </>
              ) : (
                "Save year"
              )}
            </button>
          </div>
          <p className="mt-2 text-base text-white/40">Leave blank to clear the year.</p>
        </section>

        {/* TAGS */}
        <section className="mt-12">
          <h2 className="text-[15px] font-semibold uppercase tracking-[0.12em] text-white/40 mb-4">
            Tags
          </h2>

          {tags.length > 0 ? (
            <div className="flex flex-wrap gap-2.5 mb-7" data-testid="current-tags">
              {tags.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-2 rounded-full bg-white/[0.12] py-2 pl-4 pr-2 text-lg font-medium text-white"
                  data-testid="current-tag-chip"
                >
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: chipColor(t) }} />
                  {t}
                  <button
                    onClick={() => removeCurrentTag(t)}
                    className="grid h-8 w-8 place-items-center rounded-full bg-white/10 hover:bg-white/25 transition-colors"
                    aria-label={`Remove ${t}`}
                    data-testid="remove-tag-button"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </span>
              ))}
            </div>
          ) : (
            <p className="mb-7 text-lg text-white/50">No tags yet. Add one below.</p>
          )}

          {paletteTags.length > 0 && (
            <div className="mb-7">
              <p className="mb-3 text-[15px] font-medium text-white/50">Tap to add an existing tag</p>
              <div className="flex flex-wrap gap-2.5" data-testid="tag-palette">
                {paletteTags.map((t) => (
                  <button
                    key={t}
                    onClick={() => applyTag(t)}
                    className="inline-flex items-center gap-2 rounded-full bg-white/[0.08] py-2.5 pl-3.5 pr-4 text-lg font-medium text-white ring-1 ring-white/10 hover:bg-white/[0.16] transition-colors"
                    data-testid="palette-tag-button"
                  >
                    <Plus className="h-4 w-4 text-[#0A84FF]" />
                    {t}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-3xl bg-[#1c1c1e] p-5 ring-1 ring-white/10">
            <p className="mb-3 text-[15px] font-medium text-white/50">Make a new tag</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleCreateTag()}
                placeholder="Type a tag name…"
                className="h-14 flex-1 rounded-2xl bg-white/[0.06] px-5 text-xl text-white placeholder-white/35 outline-none ring-1 ring-white/10 focus:ring-2 focus:ring-[#0A84FF] transition-all"
                data-testid="new-tag-input"
              />
              <button
                onClick={handleCreateTag}
                disabled={!newTag.trim()}
                className="h-14 rounded-2xl bg-[#0A84FF] px-8 text-lg font-semibold text-white hover:bg-[#409CFF] transition-colors disabled:opacity-40"
                data-testid="create-tag-button"
              >
                Create
              </button>
            </div>
          </div>
        </section>

        {/* SPLIT */}
        <section className="mt-12 border-t border-white/10 pt-9">
          <h2 className="text-xl font-semibold text-white mb-1.5">One tape, many videos?</h2>
          <p className="mb-4 text-lg text-white/50">
            If this video has several separate recordings, cut it into multiple named videos.
          </p>
          <button
            onClick={() => onSplit(video)}
            className="inline-flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-white/[0.08] text-lg font-semibold text-white ring-1 ring-white/10 hover:bg-white/[0.16] transition-colors"
            data-testid="open-split-button"
          >
            <Scissors className="h-5 w-5 text-[#0A84FF]" />
            Split into multiple videos
          </button>
        </section>
      </main>
    </div>
  );
}
